import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPLOYEE_RANGES = [
    "1 to 9",
    "10 to 25",
    "26 to 50",
    "51 to 100",
    "100 plus",
];

const TURNOVER_RANGES = [
    "Under AUD $1m",
    "AUD $1m to $5m",
    "AUD $5m to $20m",
    "AUD $20m to $50m",
    "AUD $50m to $100m",
    "AUD $100m plus",
];

export default function BookingForm({
    eyebrow = "Get in touch",
    heading = "Book a call or send an enquiry",
    subheading = "Share a few details and we'll be in touch to confirm a suitable time.",
    successTitle = "Thanks. We've received your request.",
    successBody = "A member of our team will be in touch shortly to confirm a suitable time. In the meantime, feel free to explore the Helm Framework or the AI Opportunity Sprint.",
    source = "contact_form",
    showWebsite = true,
    submitLabel = "Send enquiry",
    microReassurance = null,
    onSuccess,
}) {
    const [state, setState] = useState({
        full_name: "",
        business_email: "",
        phone: "",
        company_name: "",
        website: "",
        employees: "",
        turnover: "",
        main_challenge: "",
        preferred_time: "",
        consent: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const update = (k, v) => setState((s) => ({ ...s, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!state.consent) {
            toast.error("Please confirm consent before submitting.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/contact`, { ...state, source });
            setSuccess(true);
            toast.success(res.data?.message || "Thanks. We've received your request.");
            // Conversion event hook for analytics (gtag, GA4, Meta, etc.)
            try {
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("cap:lead-submitted", { detail: { source } }));
                    if (typeof window.gtag === "function") {
                        window.gtag("event", "generate_lead", { source });
                    }
                }
            } catch { /* ignore tracking errors */ }
            if (typeof onSuccess === "function") onSuccess(res.data);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Unable to submit. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div
                className="cap-card border-navy flex flex-col items-start gap-4"
                data-testid="booking-success"
            >
                <CheckCircle2 className="text-teal" size={36} strokeWidth={1.5} />
                <h3 className="font-serif text-navy text-3xl leading-tight">
                    {successTitle}
                </h3>
                <p className="text-slate whitespace-pre-line">
                    {successBody}
                </p>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            className="cap-card space-y-6"
            data-testid="booking-form"
            noValidate
        >
            <div>
                <div className="cap-eyebrow mb-2">{eyebrow}</div>
                <h3 className="font-serif text-navy text-3xl leading-tight">
                    {heading}
                </h3>
                <p className="text-slate mt-2 text-sm">
                    {subheading}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full name" required>
                    <input
                        type="text"
                        required
                        className="cap-input"
                        value={state.full_name}
                        onChange={(e) => update("full_name", e.target.value)}
                        data-testid="booking-full-name"
                    />
                </Field>
                <Field label="Business email" required>
                    <input
                        type="email"
                        required
                        className="cap-input"
                        value={state.business_email}
                        onChange={(e) => update("business_email", e.target.value)}
                        data-testid="booking-email"
                    />
                </Field>
                <Field label="Phone number">
                    <input
                        type="tel"
                        className="cap-input"
                        value={state.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        data-testid="booking-phone"
                    />
                </Field>
                <Field label="Company name" required>
                    <input
                        type="text"
                        required
                        className="cap-input"
                        value={state.company_name}
                        onChange={(e) => update("company_name", e.target.value)}
                        data-testid="booking-company"
                    />
                </Field>
                {showWebsite && (
                    <Field label="Website">
                        <input
                            type="url"
                            className="cap-input"
                            placeholder="https://"
                            value={state.website}
                            onChange={(e) => update("website", e.target.value)}
                            data-testid="booking-website"
                        />
                    </Field>
                )}
                <Field label="Number of employees">
                    <select
                        className="cap-input"
                        value={state.employees}
                        onChange={(e) => update("employees", e.target.value)}
                        data-testid="booking-employees"
                    >
                        <option value="">Select</option>
                        {EMPLOYEE_RANGES.map((r) => (
                            <option key={r} value={r}>{r}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Turnover range">
                    <select
                        className="cap-input"
                        value={state.turnover}
                        onChange={(e) => update("turnover", e.target.value)}
                        data-testid="booking-turnover"
                    >
                        <option value="">Select</option>
                        {TURNOVER_RANGES.map((r) => (
                            <option key={r} value={r}>{r}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Preferred time / availability">
                    <input
                        type="text"
                        className="cap-input"
                        placeholder="e.g. Tue or Thu afternoons (AEST)"
                        value={state.preferred_time}
                        onChange={(e) => update("preferred_time", e.target.value)}
                        data-testid="booking-preferred-time"
                    />
                </Field>
            </div>

            <Field label="Main challenge" required>
                <textarea
                    required
                    rows={4}
                    className="cap-input"
                    placeholder="What is slowing your team down today?"
                    value={state.main_challenge}
                    onChange={(e) => update("main_challenge", e.target.value)}
                    data-testid="booking-challenge"
                />
            </Field>

            <label className="flex items-start gap-3 text-xs text-slate leading-relaxed">
                <input
                    type="checkbox"
                    checked={state.consent}
                    onChange={(e) => update("consent", e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-navy"
                    data-testid="booking-consent"
                />
                <span>
                    I agree that Chief AI Partners may contact me about this enquiry.
                    See our <a href="/privacy" className="underline hover:text-navy">Privacy Policy</a>.
                </span>
            </label>

            {microReassurance && (
                <p
                    className="text-xs text-slate leading-relaxed -mb-2"
                    data-testid="booking-micro-reassurance"
                >
                    {microReassurance}
                </p>
            )}

            <button
                type="submit"
                disabled={submitting}
                className="cap-btn-primary w-full md:w-auto"
                data-testid="booking-submit-btn"
            >
                {submitting ? "Submitting..." : submitLabel}
            </button>
        </form>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="cap-label">
                {label}
                {required ? " *" : ""}
            </span>
            {children}
        </label>
    );
}
